import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { HandHeart, ChevronLeft, ChevronRight, Check, AlertCircle } from "lucide-react";
import { AssetTypeFields } from "./AssetTypeFields";

interface ContributingAssetsWizardProps {
  workflow: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ContributingAssetsWizard({ workflow, open, onOpenChange }: ContributingAssetsWizardProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedUna, setSelectedUna] = useState<any>(null);
  const [unas, setUnas] = useState<any[]>([]);
  const [unaMembers, setUnaMembers] = useState<any[]>([]);
  const [formData, setFormData] = useState<Record<string, any>>({
    contribution_date: new Date().toISOString().split('T')[0],
  });
  const [showCustomAssetDialog, setShowCustomAssetDialog] = useState(false);
  const [showManageAssetsDialog, setShowManageAssetsDialog] = useState(false);
  const [customAssetName, setCustomAssetName] = useState("");
  const [currentUserProfile, setCurrentUserProfile] = useState<any>(null);

  // Load user's UNAs (query by treasury_secretary_id, not una_members)
  useEffect(() => {
    const loadUserUnas = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('unas')
        .select('id, name, state')
        .eq('treasury_secretary_id', user.id);

      if (error) {
        console.error("Error loading UNAs:", error);
        return;
      }

      setUnas(data || []);
      if (data && data.length === 1) {
        setSelectedUna(data[0]);
        loadUnaMembers(data[0].id);
      }
    };

    if (open) {
      loadUserUnas();
    }
  }, [open]);

  // Auto-populate contributor name and address from current user profile
  useEffect(() => {
    async function loadCurrentUser() {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      
      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name, email, address, city, state, zip, country')
        .eq('id', user.id)
        .single();
        
      if (profile) {
        setCurrentUserProfile(profile);
        
        // Build full address string from profile fields
        const addressParts = [
          profile.address,
          [profile.city, profile.state, profile.zip].filter(Boolean).join(', ')
        ].filter(Boolean);
        if (profile.country && profile.country !== 'US') {
          addressParts.push(profile.country);
        }
        const fullAddress = addressParts.join(', ');
        
        setFormData(prev => ({
          ...prev,
          contributor_name: profile.full_name || prev.contributor_name,
          contributor_address: fullAddress || prev.contributor_address
        }));
      }
    }
    
    if (open) {
      loadCurrentUser();
    }
  }, [open]);

  // Load UNA members when UNA selected
  const loadUnaMembers = async (unaId: string) => {
    const { data } = await supabase
      .from('una_members')
      .select('*')
      .eq('una_id', unaId)
      .eq('status', 'active');
    
    if (data) {
      setUnaMembers(data);
    }
  };

  // Auto-populate receiving_entity when UNA is selected
  useEffect(() => {
    if (selectedUna?.name) {
      setFormData(prev => ({
        ...prev,
        receiving_entity: selectedUna.name
      }));
    }
  }, [selectedUna]);

  // Fetch custom asset types
  const { data: customAssetTypes = [] } = useQuery({
    queryKey: ["customAssetTypes", selectedUna?.id],
    queryFn: async () => {
      if (!selectedUna?.id) return [];
      const { data, error } = await supabase
        .from("custom_asset_types")
        .select("*")
        .eq("una_id", selectedUna.id)
        .order("asset_type_name");
      if (error) throw error;
      return data;
    },
    enabled: !!selectedUna?.id,
  });

  // Add custom asset type mutation
  const addCustomAssetType = useMutation({
    mutationFn: async (assetTypeName: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const { data, error } = await supabase
        .from("custom_asset_types")
        .insert({
          una_id: selectedUna.id,
          asset_type_name: assetTypeName.trim(),
          created_by: user.id,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["customAssetTypes", selectedUna?.id] });
      handleFieldChange('asset_type', `Other - ${data.asset_type_name}`);
      setShowCustomAssetDialog(false);
      setCustomAssetName("");
      toast.success(`"${data.asset_type_name}" added successfully`);
    },
  });

  // Delete custom asset type mutation
  const deleteCustomAssetType = useMutation({
    mutationFn: async (assetId: string) => {
      const { error } = await supabase
        .from("custom_asset_types")
        .delete()
        .eq("id", assetId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customAssetTypes", selectedUna?.id] });
      toast.success("Asset type deleted successfully");
    },
  });

  const handleFieldChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    // Validation
    if (currentStep === 1) {
      const allChecked = (workflow.prerequisites || []).every((_, idx) => 
        formData[`prereq_${idx}`] === true
      );
      if (!allChecked) {
        toast.error("Please check all prerequisites");
        return;
      }
    }

    if (currentStep === 2) {
      // Validate required fields
      if (!formData.contributor_name) {
        toast.error("Please enter contributor name");
        return;
      }
      if (!formData.contributor_role) {
        toast.error("Please select contributor role");
        return;
      }
      if (!formData.contributor_address) {
        toast.error("Please enter contributor address");
        return;
      }
      if (!formData.receiving_entity) {
        toast.error("Please enter receiving entity");
        return;
      }
      if (!formData.asset_type) {
        toast.error("Please select an asset type");
        return;
      }
      if (!selectedUna) {
        toast.error("Please select a UNA");
        return;
      }

      // Block retirement account transfers
      if (formData.is_retirement_account === true) {
        toast.error("Retirement accounts cannot be transferred through this workflow. Please consult a financial advisor.");
        return;
      }
    }

    setCurrentStep(prev => Math.min(3, prev + 1));
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(1, prev - 1));
  };

  const handleLaunch = async () => {
    if (!selectedUna) {
      toast.error("No UNA selected");
      return;
    }

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Create the workflow task
      const taskTitle = `Contributing Assets - ${formData.asset_type}`;
      const taskDescription = `Private contribution of ${formData.asset_type} to ${selectedUna.name}`;

      const { data: task, error: taskError } = await supabase
        .from('custom_tasks')
        .insert({
          task_title: taskTitle,
          task_description: taskDescription,
          user_id: user.id,
          una_id: selectedUna.id,
          status: 'in_progress',
          category: 'governance',
          workflow_id: workflow.id,
          is_from_workflow: true,
          workflow_completion_percentage: 0
        })
        .select()
        .single();

      if (taskError) throw taskError;

      // Activate the workflow - include UNA data in workflow_data
      const { error: activationError } = await supabase
        .from('task_workflows')
        .insert({
          task_id: task.id,
          workflow_id: workflow.id,
          una_id: selectedUna.id,
          current_phase: 1,
          workflow_data: {
            ...formData,
            una_name: selectedUna.name,
            una_state: selectedUna.state || '',
          }
        });

      if (activationError) throw activationError;

      toast.success("Workflow launched successfully!");
      onOpenChange(false);
      // Navigate to Workflows page showing the newly launched workflow
      navigate(`/workflows?tab=my-workflows&launched=1&wid=${workflow.id}`);
    } catch (error: any) {
      console.error('Error launching workflow:', error);
      toast.error(error.message || "Failed to launch workflow");
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    if (currentStep === 1) {
      // Prerequisites
      return (
        <div className="space-y-4">
          <div className="space-y-3">
            {(workflow.prerequisites || []).map((prereq: string, idx: number) => (
              <div key={idx} className="flex items-start space-x-3">
                <Checkbox
                  id={`prereq_${idx}`}
                  checked={formData[`prereq_${idx}`] || false}
                  onCheckedChange={(checked) => handleFieldChange(`prereq_${idx}`, checked)}
                />
                <Label htmlFor={`prereq_${idx}`} className="font-normal leading-relaxed">
                  {prereq}
                </Label>
              </div>
            ))}
          </div>

          <div className="mt-6 p-4 bg-muted/50 rounded-lg space-y-2">
            <h4 className="font-semibold flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              Understanding Ecclesiastical Contributions
            </h4>
            <p className="text-sm text-muted-foreground">
              When you contribute assets to your UNA, you're not making a "donation" or "gift" under public law. 
              You're making a <strong>private member contribution</strong> within an ecclesiastical jurisdiction. 
              This maintains complete privacy and keeps the transaction outside state and federal oversight.
            </p>
          </div>

          <div className="p-4 bg-muted/50 rounded-lg space-y-2">
            <h4 className="font-semibold">The "Pitch and Catch" Pattern</h4>
            <p className="text-sm text-muted-foreground">
              Phase 1 documents (PITCH) authorize the contribution from the member's side. 
              Phase 2 documents (CATCH) accept the contribution on the UNA's behalf. 
              This two-sided process creates a complete private contract under ecclesiastical law.
            </p>
          </div>
        </div>
      );
    }

    if (currentStep === 2) {
      // Contribution Information
      return (
        <div className="space-y-6">
          {/* UNA Selection or Display */}
          {unas.length > 1 ? (
            <div>
              <Label htmlFor="una_select">Select UNA *</Label>
              <Select 
                value={selectedUna?.id || ''} 
                onValueChange={(value) => {
                  const una = unas.find(u => u.id === value);
                  setSelectedUna(una);
                  if (una) loadUnaMembers(una.id);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select UNA" />
                </SelectTrigger>
                <SelectContent>
                  {unas.map(una => (
                    <SelectItem key={una.id} value={una.id}>{una.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div>
              <Label>Contributing To</Label>
              <div className="p-3 bg-muted rounded-md font-medium">
                {selectedUna?.name || 'Loading...'}
              </div>
            </div>
          )}

          {/* Contributor Information */}
          <div>
            <Label>Contributor Name *</Label>
            <Input 
              value={formData.contributor_name || currentUserProfile?.full_name || ''} 
              disabled
              className="bg-muted"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Auto-filled from your profile
            </p>
          </div>

          <div>
            <Label>Contributor Role *</Label>
            <Select value={formData.contributor_role || ''} onValueChange={(v) => handleFieldChange('contributor_role', v)}>
              <SelectTrigger>
                <SelectValue placeholder="Select your role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Secretary Treasurer">Secretary Treasurer</SelectItem>
                <SelectItem value="Managing Member">Managing Member</SelectItem>
                <SelectItem value="Board Member">Board Member</SelectItem>
                <SelectItem value="General Member">General Member</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Contributor Address *</Label>
            <Input 
              value={formData.contributor_address || ''} 
              onChange={(e) => handleFieldChange('contributor_address', e.target.value)}
              placeholder="123 Main St, City, State ZIP"
            />
          </div>

          <div>
            <Label>Receiving Entity *</Label>
            <Input 
              value={formData.receiving_entity || selectedUna?.name || ''} 
              onChange={(e) => handleFieldChange('receiving_entity', e.target.value)}
              placeholder="Entity receiving the contribution"
            />
          </div>

          {/* Asset Type Selection with Custom Types */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="asset_type">Asset Type *</Label>
              <div className="flex gap-2 text-xs">
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="h-auto p-0"
                  onClick={() => setShowCustomAssetDialog(true)}
                >
                  + Add Custom Type
                </Button>
                {customAssetTypes.length > 0 && (
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="h-auto p-0"
                    onClick={() => setShowManageAssetsDialog(true)}
                  >
                    Manage Types
                  </Button>
                )}
              </div>
            </div>
            
            <Select value={formData.asset_type || ''} onValueChange={(v) => handleFieldChange('asset_type', v)}>
              <SelectTrigger>
                <SelectValue placeholder="Select asset type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Real Estate - Primary Residence">Real Estate - Primary Residence</SelectItem>
                <SelectItem value="Real Estate - Secondary Residence">Real Estate - Secondary Residence</SelectItem>
                <SelectItem value="Real Estate - Commercial (non-rental)">Real Estate - Commercial (non-rental)</SelectItem>
                <SelectItem value="Real Estate - Land">Real Estate - Land</SelectItem>
                <SelectItem value="Real Estate - Agricultural">Real Estate - Agricultural</SelectItem>
                <SelectItem value="Vehicle - Auto/Truck">Vehicle - Auto/Truck</SelectItem>
                <SelectItem value="Vehicle - RV/Boat">Vehicle - RV/Boat</SelectItem>
                <SelectItem value="Vehicle - Motorcycle">Vehicle - Motorcycle</SelectItem>
                <SelectItem value="Vehicle - Other">Vehicle - Other</SelectItem>
                <SelectItem value="Cash/Bank Account">Cash/Bank Account</SelectItem>
                <SelectItem value="Investment/Brokerage Account">Investment/Brokerage Account</SelectItem>
                <SelectItem value="Personal Property">Personal Property</SelectItem>
                <SelectItem value="Intellectual Property">Intellectual Property</SelectItem>
                <SelectItem value="Digital Assets/Cryptocurrency">Digital Assets/Cryptocurrency</SelectItem>
                <SelectItem value="Precious Metals">Precious Metals</SelectItem>
                <SelectItem value="Other Asset">Other Asset</SelectItem>
                
                {/* Custom asset types */}
                {customAssetTypes.length > 0 && (
                  <>
                    <SelectItem disabled value="separator">──────────</SelectItem>
                    {customAssetTypes.map((type: any) => (
                      <SelectItem key={type.id} value={`Other - ${type.asset_type_name}`}>
                        Other - {type.asset_type_name}
                      </SelectItem>
                    ))}
                  </>
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Warning Banner - Only for Real Estate */}
          {formData.asset_type?.startsWith('Real Estate') && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <div className="space-y-2">
                  <p className="font-semibold">Cash-Flowing Rental Properties</p>
                  <p className="text-sm">
                    If you're contributing a rental or cash-flowing property, you should first 
                    establish an LLC owned by your UNA, then title the property to that LLC. 
                    This workflow is for properties transferred directly to the UNA.
                  </p>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Contributor Information */}
          <div className="space-y-4 border-t pt-4">
            <h4 className="font-semibold">Contributor Information</h4>
            
            <div>
              <Label htmlFor="contributor_name">Contributor Name *</Label>
              <Input
                id="contributor_name"
                value={formData.contributor_name || ''}
                onChange={(e) => handleFieldChange('contributor_name', e.target.value)}
                required
              />
            </div>

            <div>
              <Label htmlFor="contributor_role">Contributor Role</Label>
              <Input
                id="contributor_role"
                value={formData.contributor_role || 'Secretary Treasurer'}
                onChange={(e) => handleFieldChange('contributor_role', e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="contribution_date">Contribution Date *</Label>
              <Input
                id="contribution_date"
                type="date"
                value={formData.contribution_date || ''}
                onChange={(e) => handleFieldChange('contribution_date', e.target.value)}
                required
              />
            </div>
          </div>

          {/* Asset-Specific Fields */}
          {formData.asset_type && (
            <div className="border-t pt-4">
              <h4 className="font-semibold mb-4">Asset Details</h4>
              <AssetTypeFields
                assetType={formData.asset_type}
                formData={formData}
                onChange={handleFieldChange}
                unaMembers={unaMembers}
              />
            </div>
          )}

          {/* Contribution Method */}
          <div className="border-t pt-4 space-y-4">
            <div>
              <Label htmlFor="contribution_method">Contribution Method *</Label>
              <Select value={formData.contribution_method || ''} onValueChange={(v) => handleFieldChange('contribution_method', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Private Member Contribution">Private Member Contribution</SelectItem>
                  <SelectItem value="Private Assignment of Member Interest">Private Assignment of Member Interest</SelectItem>
                  <SelectItem value="Private Stewardship Assignment">Private Stewardship Assignment</SelectItem>
                  <SelectItem value="Ecclesiastical Contribution">Ecclesiastical Contribution</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="contribution_purpose">Overall Purpose</Label>
              <Textarea
                id="contribution_purpose"
                value={formData.contribution_purpose || ''}
                onChange={(e) => handleFieldChange('contribution_purpose', e.target.value)}
                rows={3}
                placeholder="Describe the purpose of this contribution"
              />
            </div>
          </div>
        </div>
      );
    }

    if (currentStep === 3) {
      // Review & Launch
      return (
        <div className="space-y-6">
          <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
            <h4 className="font-semibold">Workflow Summary</h4>
            <div className="space-y-2 text-sm">
              <div><strong>Workflow:</strong> Contributing Assets</div>
              <div><strong>Asset Type:</strong> {formData.asset_type}</div>
              <div><strong>Contribution Date:</strong> {formData.contribution_date}</div>
              <div><strong>UNA:</strong> {selectedUna?.una_name}</div>
              <div><strong>Phases:</strong> 3</div>
              <div><strong>Estimated Time:</strong> 30-90 minutes</div>
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="font-semibold">What Happens Next</h4>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <Check className="h-4 w-4 mt-0.5 text-green-600" />
                <span>A task will be created in your task list titled "Contributing Assets - {formData.asset_type}"</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-4 w-4 mt-0.5 text-green-600" />
                <span>You'll be guided through 3 phases: Phase 1 (PITCH documents), Phase 2 (CATCH documents), Phase 3 (Record & Update)</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-4 w-4 mt-0.5 text-green-600" />
                <span>Documents will be generated automatically based on your input</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-4 w-4 mt-0.5 text-green-600" />
                <span>All signatures will be collected natively within the platform</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-4 w-4 mt-0.5 text-green-600" />
                <span>Asset stewardship will be established in your UNA records</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-4 w-4 mt-0.5 text-green-600" />
                <span>You can pause and resume at any time</span>
              </li>
            </ul>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HandHeart className="h-5 w-5" />
              {workflow.title}
            </DialogTitle>
          </DialogHeader>

        {/* Step Indicators */}
        <div className="flex items-center justify-center gap-2 py-4">
          {[1, 2, 3].map((step) => (
            <div key={step} className="flex items-center">
              <div className={`
                flex items-center justify-center w-8 h-8 rounded-full
                ${currentStep === step ? 'bg-primary text-primary-foreground' : 
                  currentStep > step ? 'bg-green-600 text-white' : 'bg-muted text-muted-foreground'}
              `}>
                {currentStep > step ? <Check className="h-4 w-4" /> : step}
              </div>
              {step < 3 && (
                <div className={`w-12 h-0.5 ${currentStep > step ? 'bg-green-600' : 'bg-muted'}`} />
              )}
            </div>
          ))}
        </div>

        <div className="text-center text-sm font-medium mb-4">
          Step {currentStep} of 3: {
            currentStep === 1 ? 'Prerequisites' :
            currentStep === 2 ? 'Contribution Information' :
            'Review & Launch'
          }
        </div>

        <ScrollArea className="max-h-[50vh]">
          <div className="px-1">
            {renderStep()}
          </div>
        </ScrollArea>

        {/* Navigation */}
        <div className="flex justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>

          {currentStep < 3 ? (
            <Button onClick={handleNext}>
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleLaunch} disabled={loading}>
              {loading ? "Launching..." : "Launch Workflow"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>

    {/* Add Custom Asset Type Dialog */}
    <Dialog open={showCustomAssetDialog} onOpenChange={setShowCustomAssetDialog}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Custom Asset Type</DialogTitle>
          <DialogDescription>
            Create a custom asset type that doesn't fit the standard categories.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="custom_asset_name">Asset Type Name *</Label>
            <Input
              id="custom_asset_name"
              value={customAssetName}
              onChange={(e) => setCustomAssetName(e.target.value)}
              placeholder="e.g., Artwork Collection, Mineral Rights"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowCustomAssetDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => addCustomAssetType.mutate(customAssetName)}
              disabled={!customAssetName.trim() || addCustomAssetType.isPending}
            >
              Add Asset Type
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Manage Custom Asset Types Dialog */}
    <Dialog open={showManageAssetsDialog} onOpenChange={setShowManageAssetsDialog}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Manage Custom Asset Types</DialogTitle>
          <DialogDescription>
            Delete custom asset types you no longer need.
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[400px]">
          <div className="space-y-2">
            {customAssetTypes.map((type: any) => (
              <div key={type.id} className="flex items-center justify-between p-2 border rounded-lg">
                <span className="text-sm">{type.asset_type_name}</span>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => deleteCustomAssetType.mutate(type.id)}
                  disabled={deleteCustomAssetType.isPending}
                >
                  Delete
                </Button>
              </div>
            ))}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
    </>
  );
}